import { Button, Card, CardActions, CardContent, Checkbox, colors, FormControlLabel, Grid, Modal, TextField, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { KeyboardDatePicker } from '@material-ui/pickers';
import clsx from 'clsx';
import { ErrorMessage } from 'components';
import moment from 'moment';
import React, { Fragment, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Account } from 'services';
import capitalise from 'utils/capitalise';
import useErrorCatcher from "utils/useErrorCatcher";
import validate from "validate.js";
import useRouter from 'utils/useRouter';

validate.validators.accounts = function (value, options, key, attributes) {
  let { type } = attributes;
  switch (key) {
    case "name_en":
      if ((type === "user" || type === "teacher") && !value) return "is required";
      break;
    case "gender":
      if (type === "user" && !value) return "is required";
      break;
    case "birthdate":
      if (type === "user" && !value) return "is required";
      break;
    default: break;
  }
}

const useStyles = makeStyles(theme => ({
  root: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    outline: 'none',
    boxShadow: theme.shadows[20],
    width: 450,
    maxHeight: '100%',
    overflowY: 'auto',
    maxWidth: '100%'
  },
  container: {
    marginTop: theme.spacing(3)
  },
  actions: {
    justifyContent: 'flex-end'
  },
  saveButton: {
    color: theme.palette.white,
    backgroundColor: colors.green[600],
    '&:hover': {
      backgroundColor: colors.green[900]
    }
  },
  datepicker: {
    marginTop: 0
  }
}));

const schema = {
  email: {
    email: true,
    presence: { allowEmpty: false, message: "is required" },
  },
  displayname: {
    presence: { allowEmpty: false, message: "is required" }
  },
  password: {
    presence: { allowEmpty: false, message: "is required" },
  },
  confirm_password: {
    presence: { allowEmpty: false, message: "is required" },
    equality: { attribute: "password", message: "does not match password" }
  },
  type: {
    presence: { allowEmpty: false, message: "is required" }
  },
  username: {
    presence: { allowEmpty: false, message: "is required" }
  },
  name_en: {
    accounts: true
  },
  birthdate: {
    accounts: true
  },
  gender: {
    accounts: true
  },
};

const INIT_VALUES = {
  displayname: "",
  email: "",
  password: "",
  confirm_password: "",
  type: "user",
  username: "",
  gender: "male",
  birthdate: moment(),
  phone: "",
  selectedRoles: [],
  name_en: "",
  name_zh: "",
  title: "",
};
let mounted = false;

const CreateModal = props => {
  let { onCreate, open, onClose, className, ...rest } = props;

  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const runAsyncBlock = useErrorCatcher(error => setErrorMessage(error && capitalise(error.message)));
  const roles = useSelector(state => state.models.roles);
  const [errorMessage, setErrorMessage] = useState(null);
  const router = useRouter();

  const [formState, setFormState] = useState({
    isValid: false,
    values: { ...INIT_VALUES },
    touched: {},
    errors: {}
  });
  const selectedRoles = formState.values.selectedRoles;

  useEffect(() => {
    setFormState({
      isValid: false,
      values: { ...INIT_VALUES },
      touched: {},
      errors: {}
    });
  }, [open]);

  useEffect(() => {
    mounted = true;

    return () => { mounted = false; }
  }, []);

  useEffect(() => {
    const errors = validate(formState.values, schema);

    setFormState(formState => ({
      ...formState,
      errors: errors || {}
    }));
  }, [formState.values]);

  if (!open) {
    return null;
  }


  const handleSubmit = event => {
    event.preventDefault();

    setFormState(formState => ({
      ...formState,
      touched: {
        // to show error message
        displayname: true,
        username: true,
        email: true,
        password: true,
        name_en: true,
        gender: true,
        birthdate: true
      }
    }))
    if (Object.keys(formState.errors).length) return;

    setLoading(true);
    runAsyncBlock(async () => {
      const { displayname, email, phone, type, username, password, selectedRoles, name_en, name_zh, title, gender, birthdate } = formState.values;
      let data = { displayname, email, phone, type, username, password };
      switch (data.type) {
        case "user":
          data = {
            ...data,
            student: {
              name_en, name_zh, gender, birthdate
            }
          }
          break;
        case "teacher":
          data = {
            ...data,
            teacher: {
              name_en, title
            }
          }
          break;
        case "admin":
          data = {
            ...data,
            roles: selectedRoles
          }
          break;
        default: break;
      }
      const { model } = await Account.create(data);
      onCreate();
      router.history.push(`/accounts/${model.id}/summary`);

      // onClose();
    }).finally(() => {
      mounted && setLoading(false);
    });

  }

  const handleFieldChange = event => {
    event.persist();
    setFormState(formState => ({
      ...formState,
      values: {
        ...formState.values,
        [event.target.name]: event.target.type === 'checkbox' ? event.target.checked : event.target.value
      },
      touched: {
        ...formState.touched,
        [event.target.name]: true
      }
    }));
  };

  const handleSelectOne = (event, name) => {
    const selectedIndex = selectedRoles.indexOf(name);
    let newSelectedRoles = [];

    if (selectedIndex === -1) {
      newSelectedRoles = newSelectedRoles.concat(selectedRoles, name);
    } else if (selectedIndex === 0) {
      newSelectedRoles = newSelectedRoles.concat(
        selectedRoles.slice(1)
      );
    } else if (selectedIndex === selectedRoles.length - 1) {
      newSelectedRoles = newSelectedRoles.concat(
        selectedRoles.slice(0, -1)
      );
    } else if (selectedIndex > 0) {
      newSelectedRoles = newSelectedRoles.concat(
        selectedRoles.slice(0, selectedIndex),
        selectedRoles.slice(selectedIndex + 1)
      );
    }

    setFormState(formState => ({
      ...formState,
      values: {
        ...formState.values,
        selectedRoles: newSelectedRoles
      }
    }));
  };

  const handleDateChange = (date, field) => {
    setFormState(formState => ({
      ...formState,
      values: {
        ...formState.values,
        [field]: date
      },
      touched: {
        ...formState.touched,
        [field]: true
      }
    }));
  };

  const hasError = field => formState.touched[field] && formState.errors[field] ? true : false;

  return (
    <Modal open={open}>
      <Card {...rest} className={clsx(classes.root, className)}>
        <form>
          <CardContent>
            <Typography align="center" gutterBottom variant="h3">
              Create Account
            </Typography>
            <ErrorMessage className={classes.error} message={errorMessage} />
            <Grid className={classes.container} container spacing={3}>
              <Grid item md={12} xs={12}>
                <CustTextField name="username" label="Username" formState={formState} handleFieldChange={handleFieldChange} />
              </Grid>
              <Grid item md={12} xs={12}>
                <CustTextField name="displayname" label="Display Name" formState={formState} handleFieldChange={handleFieldChange} />
              </Grid>
              <Grid item md={12} xs={12}>
                <CustTextField name="email" label="Email" formState={formState} handleFieldChange={handleFieldChange} />
              </Grid>
              <Grid item md={6} xs={6}>
                <CustTextField type="password" name="password" label="Password" formState={formState} handleFieldChange={handleFieldChange} />
              </Grid>
              <Grid item md={6} xs={6}>
                <CustTextField type="password" name="confirm_password" label="Confirm Password" formState={formState} handleFieldChange={handleFieldChange} />
              </Grid>
              <Grid item md={12} xs={12}>
                <CustTextField name="phone" label="Phone" formState={formState} handleFieldChange={handleFieldChange} />
              </Grid>
              <Grid item md={12} xs={12}>
                <CustTextField dropdown name="type" label="Type" formState={formState} handleFieldChange={handleFieldChange} options={[{ label: "User", value: "user" }, { label: "Teacher", value: "teacher" }, { label: "Admin", value: "admin" }]} />
              </Grid>
              {formState.values.type === "admin" &&
                roles.filter(role => role.name !== "superadmin").map(role => (
                  <Grid item md={4} xs={4} key={role.id}>
                    <FormControlLabel
                      control={<Checkbox
                        checked={selectedRoles.indexOf(role.name) !== -1}
                        color="primary"
                        onChange={e => handleSelectOne(e, role.name)}
                        value={selectedRoles.indexOf(role.id) !== -1}
                      />}
                      label={role.label}
                    />
                  </Grid>
                ))
              }
              {formState.values.type === "user" &&
                <Fragment>
                  <Grid item md={6} xs={6}>
                    <CustTextField name="name_en" label="Student Name" formState={formState} handleFieldChange={handleFieldChange} />
                  </Grid>
                  <Grid item md={6} xs={6}>
                    <CustTextField name="name_zh" label="Student Name (ZH)" formState={formState} handleFieldChange={handleFieldChange} />
                  </Grid>
                  <Grid item md={6} xs={6}>
                    <CustTextField name="gender" label="Gender" formState={formState} handleFieldChange={handleFieldChange} dropdown options={[{ label: "Male", value: "male" }, { label: "Female", value: "female" }, { label: "Others", value: "Other" }]} />
                  </Grid>
                  <Grid item md={6} xs={6}>
                    <KeyboardDatePicker
                      className={classes.datepicker}
                      fullWidth
                      disableFuture
                      variant="inline"
                      format="YYYY/MM/DD"
                      margin="normal"
                      id="birthdate"
                      label="Date of Birth"
                      helperText={hasError("birthdate") ? formState.errors.birthdate[0] : null}
                      value={formState.values.birthdate}
                      onChange={(date) =>
                        handleDateChange(date, "birthdate")
                      }
                      KeyboardButtonProps={{
                        'aria-label': 'change date',
                      }}
                      error={hasError("birthdate")}
                      InputLabelProps={{ shrink: true, classes: { root: classes.label } }}
                    />
                  </Grid>
                </Fragment>
              }
              {formState.values.type === "teacher" &&
                <Fragment>
                  <Grid item md={6} xs={6}>
                    <CustTextField name="name_en" label="Teacher Name" formState={formState} handleFieldChange={handleFieldChange} />
                  </Grid>
                  <Grid item md={6} xs={6}>
                    <CustTextField name="title" label="Teacher Title" formState={formState} handleFieldChange={handleFieldChange} />
                  </Grid>
                </Fragment>
              }
            </Grid>
          </CardContent>
          <CardActions className={classes.actions}>
            <Button onClick={onClose} variant="contained">Close</Button>
            <Button className={classes.saveButton} onClick={handleSubmit} variant="contained" disabled={loading} >Create</Button>
          </CardActions>
        </form>
      </Card>
    </Modal>
  );
}



const CustTextField = props => {
  let { label, name, formState, handleFieldChange, type, dropdown, options, required } = props;
  const hasError = field => formState.touched[field] && formState.errors[field] ? true : false;

  return (
    <TextField
      fullWidth
      label={label}
      helperText={hasError(name) ? formState.errors[name][0] : null}
      name={name}
      onChange={handleFieldChange}
      value={formState.values[name]}
      error={hasError(name)}
      variant="outlined"
      type={type}
      select={dropdown}
      SelectProps={{ native: true }}
      InputLabelProps={{ shrink: dropdown }}
      required={required}
    >
      {options && options.length > 0 && options.map((opt, index) => (
        <option key={index} value={opt.value}>{opt.label}</option>
      ))
      }
    </TextField>
  );
}


export default CreateModal;